import * as React from "react";
import { Fragment, useState } from "react";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { Helmet } from "react-helmet";
import { Avatar, Box, Chip, Paper, Tooltip, Typography } from "@mui/material";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { StyledTab, StyledTabs } from "components/Tab";
import { useClusterDetail } from "utils/query";
import { ChangeProtectionGuardProvider } from "components/ChangeProtectionGuard";
import LinearProgress from "components/LinearProgress";
import { Message } from "components/SnackbarMessage";
import ActionsHistoryTab from "./ClusterDetailsTabs/ActionsHistoryTab";
import Heading from "components/Heading";
import { a11yProps, ColumnHeader } from "./ClusterDetailsTabs/TabPanel";
import DetailsTab from "./ClusterDetailsTabs/DetailsTab";
import InfoColumnTab from "./ClusterDetailsTabs/InfoColumnTab";
import PodsTab from "./ClusterDetailsTabs/PodsTab";
import SplitInfoColumnTab from "./ClusterDetailsTabs/SplitInfoColumnTab";
import ClipboardCopy from "../../components/ClipboardCopy";
import StatusTabNew from "./ClusterDetailsTabs/StatusTabNew";
import ApplicationPropertiesTab from "./ClusterDetailsTabs/ApplicationPropertiesTab";
import ResourcesTab from "./ClusterDetailsTabs/ResourcesTab";
import { CLUSTER_ACTION_TYPE, CLUSTER_STATE } from "constants/common";
import UrlsTab from "./ClusterDetailsTabs/UrlsTab";
import OutlinedButton from "components/buttons/OutlinedButton";
import { getColorFromState, getEnvironmentIcon } from "utils/utils";
import { isWorkerHostingEnvironment } from "utils/saastypes";
import {
  HostedWorkersComputeChip,
  HostingChip,
} from "components/chips/HostingChip";
import { MultiRegionChip } from "components/chips/MultiRegionChip";
import AuthenticationTab from "./ClusterDetailsTabs/AuthenticationTab";
import { IconFromState } from "components/ClusterDetailCard/common";
import { useAuth } from "auth/AuthContext";
import { MessageContext } from "utils/MessageProvider";
import ReleaseTab from "./ClusterDetailsTabs/ReleaseTab";
import MetricsTab from "./metrics/MetricsTab";
import InfrastructureTab from "./infrastructure/InfrastructureTab";
import { CLUSTER_DETAIL_URL, UNAUTHORIZED_ROUTE_URL } from "utils/routePaths";
import { DETAILS_TAB_ID, WORKER_HOSTING_TABS } from "./constants";
import { CertificateTab } from "./ClusterDetailsTabs/CertificateTab";
import { useAuthMachine } from "auth/state/hook";
import ActionTypeSelector from "./ActionTypeSelector";
import ConnectDialog from "./connect/ConnectDialog";
import { selectedTabIndex } from "./helpers";
import { clickableChipSx } from "components/ClusterDetailCard/common";

const sxChip = {
  fontSize: "14px",
  marginLeft: "16px",
  paddingTop: "2px",
  fontWeight: 400,
};

function isPlainObject(input: any) {
  return input && !Array.isArray(input) && typeof input === "object";
}

function formatData(data: any, propRenderers: any) {
  for (let key in data) {
    if (isPlainObject(data[key])) {
      Object.keys(data[key]).forEach((subKey) => {
        data[key + "." + subKey] = data[key][subKey];
      });
    }
  }
  let output = [];
  for (let key in propRenderers) {
    if (data[key]) {
      output.push({
        name: propRenderers[key].name,
        value: propRenderers[key].renderer
          ? propRenderers[key].renderer(data[key])
          : defaultRenderer(data[key]),
      });
    }
  }
  return output;
}

function defaultRenderer(val: Array<string> | string | boolean | undefined) {
  if (Array.isArray(val)) {
    return val.map((el, index) => (
      <Fragment key={index}>
        {el}
        <br />
      </Fragment>
    ));
  } else if (typeof val === "boolean") {
    return val ? <>true</> : <>false</>;
  } else {
    return val && val !== "undefined" ? <>{val}</> : <>-</>;
  }
}

const firstPageProps: { [key: string]: any } = {
  ingressType: { name: "Ingress Type" },
  vpcId: {
    name: "VPC ID",
    renderer: (val: string) => {
      return <ClipboardCopy value={val}>{val}</ClipboardCopy>;
    },
  },
  azIds: { name: "Availability Zones" },
  loadBalancerName: {
    name: "Load Balancer ID",
    renderer: (val: string) => {
      return <ClipboardCopy value={val}>{val}</ClipboardCopy>;
    },
  },
  vpcCidr: { name: "VPC CIDR" },
  subnetIds: { name: "Main Subnet IDs" },
  cloudEnvironmentTag: {
    name: "Cloud Environment Tag",
    renderer: (val: string) => {
      return <ClipboardCopy value={val}>{val}</ClipboardCopy>;
    },
  },
  ipRanges: {
    name: "",
    renderer: (val: string) => {
      let parts = val.split(" $$ ");
      return (
        <>
          <ColumnHeader>IP Range Start</ColumnHeader>
          <Typography>{parts[0]}</Typography>
          <ColumnHeader>IP Range End</ColumnHeader>
          <Typography>{parts[1]}</Typography>
        </>
      );
    },
  },
  utilitySubnetIds: { name: "Utility Subnet IDs" },
};

const secondPageLeftProps: { [key: string]: any } = {
  "elasticCacheConfig.engine": { name: "Engine" },
  "elasticCacheConfig.engineVersion": { name: "Engine Version" },
  "elasticCacheConfig.url": {
    name: "Engine Host",
    renderer: (val: string) => {
      return <ClipboardCopy value={val}>{val}</ClipboardCopy>;
    },
  },
  "elasticCacheConfig.port": { name: "Engine Port" },
  "elasticCacheConfig.replicationGroupId": { name: "Replication Group" },
  "elasticCacheConfig.subnetGroupId": { name: "Subnet Group ID" },
  "elasticCacheConfig.numberCacheClusters": {
    name: "Cache Nodes",
  },
  "elasticCacheConfig.nodeType": { name: "Node Type" },
};

const secondPageRightProps: { [key: string]: any } = {
  "rdsConfig.pgFamily": { name: "Postgres Family" },
  "rdsConfig.engineVersion": { name: "Database Type" },
  "rdsConfig.endpoint": {
    name: "Database Endpoint",
    renderer: (val: string) => {
      return <ClipboardCopy value={val}>{val}</ClipboardCopy>;
    },
  },
  "rdsConfig.pgName": {
    name: "Postgres Name",
    renderer: (val: string) => {
      return <ClipboardCopy value={val}>{val}</ClipboardCopy>;
    },
  },
};
const MULTI_TENANT_TABS = [CLUSTER_DETAIL_URL.URLS, CLUSTER_DETAIL_URL.STATUS];

export default function ClusterDetails() {
  const { clusterName, "*": splat } = useParams();
  const [, entryId] = splat?.split("/") || [];
  const [selectedActionType, setSelectedActionType] = useState<string>("");
  const [connectOpen, setConnectOpen] = useState(false);
  const [podName, setPodName] = useState("");
  // Computed by the Pods tab, which holds a fresher list than the cached deployment info the
  // dialog can see on its own. Undefined means nobody has said, so the dialog works it out.
  const [deletingLastRunningPod, setDeletingLastRunningPod] = useState<
    boolean | undefined
  >(undefined);
  const { pathname } = useLocation();
  const basePath = `${CLUSTER_DETAIL_URL.ROOT}/${clusterName}`;
  const relativePath = pathname.split(basePath)[1];
  const { setMessage } = React.useContext(MessageContext);

  const { authService } = useAuth();
  const [
    {
      isOrkesUser,
      hasAdminOrRootRole,
      hasEngineeringProfile,
      hasGlobalBypass,
      canManageEnvironment,
      canUseSupportOperations,
    },
  ] = useAuthMachine(authService);
  const canRunCustomerManageCommand = isOrkesUser || hasAdminOrRootRole;
  const canManageCertificates = canManageEnvironment || canUseSupportOperations;

  const showError = (message: string) => {
    setMessage(new Message(message, "error"));
  };

  const showSuccess = (message: string) => {
    setMessage(new Message(message, "success"));
  };

  const {
    data: clusterDetail,
    isFetching: clusterDetailFetching,
    refetch: refetchClusterDetail,
    isMultiTenant,
    clusterWasRemoved,
  } = useClusterDetail(clusterName as string);

  const isMultiTenantAndNotOrkesUser = React.useMemo(
    () => isMultiTenant && !isOrkesUser,
    [isMultiTenant, isOrkesUser]
  );

  const maybeFilterMultiTenantTabs = React.useCallback(
    ({ id }: { id: string }) =>
      isMultiTenantAndNotOrkesUser ? MULTI_TENANT_TABS.includes(id) : true,
    [isMultiTenantAndNotOrkesUser]
  );

  const isWorkerHosting = React.useMemo(
    () => isWorkerHostingEnvironment(clusterDetail),
    [clusterDetail]
  );

  // Same shape and call sites as maybeFilterMultiTenantTabs above. Safe against the
  // hardcoded index props for the same reason that filter is: tab selection is by label
  // (StyledTabs value comes from selectedTabIndex(), which returns CLUSTER_DETAILS_TABS
  // label strings matching each StyledTab's value), and the selectedTabIndex/index pairs
  // handed to the tab components are equal by construction, so dropping entries changes
  // neither the selected tab nor which panel renders.
  const maybeFilterWorkerHostingTabs = React.useCallback(
    ({ id }: { id: string }) =>
      isWorkerHosting ? WORKER_HOSTING_TABS.includes(id) : true,
    [isWorkerHosting]
  );

  if (clusterDetail && clusterDetail.ipRangeStart && clusterDetail.ipRangeEnd) {
    (
      clusterDetail as any
    ).ipRanges = `${clusterDetail.ipRangeStart} $$ ${clusterDetail.ipRangeEnd}`;
  }

  if (clusterDetail && clusterDetail.subnets) {
    clusterDetail.subnetIds = clusterDetail.subnets
      .filter((x: any) => x.type === "MAIN")
      .map((subnet: any) => subnet.awsId);
    clusterDetail.utilitySubnetIds = clusterDetail.subnets
      .filter((x: any) => x.type === "UTILITY")
      .map((subnet: any) => subnet.awsId);
  }

  // Build network tab props dynamically — hide AWS-only fields for GCP, add Shared VPC fields when applicable
  const networkPageProps = { ...firstPageProps };
  // Worker hosting: the VNet and subnet genuinely exist and are worth showing, but there is
  // no public load balancer, no utility subnet and no AZ spread to report.
  if (isWorkerHosting) {
    delete networkPageProps.azIds;
    delete networkPageProps.loadBalancerName;
    delete networkPageProps.ipRanges;
    delete networkPageProps.utilitySubnetIds;
    networkPageProps.resourceGroup = { name: "Resource Group" };
    networkPageProps.existingSubnetId = {
      name: "Existing Subnet ID",
      renderer: (val: string) => (
        <ClipboardCopy value={val}>{val}</ClipboardCopy>
      ),
    };
  }
  if (clusterDetail?.cloudProvider === "GCP") {
    delete networkPageProps.utilitySubnetIds;
    delete networkPageProps.ipRanges;

    if (clusterDetail?.sharedVpc) {
      networkPageProps.hostProjectId = { name: "Host Project ID" };
      networkPageProps.vpcSelfLink = {
        name: "VPC Self-Link",
        renderer: (val: string) => (
          <ClipboardCopy value={val}>{val}</ClipboardCopy>
        ),
      };
      networkPageProps.subnetSelfLink = {
        name: "Subnet Self-Link",
        renderer: (val: string) => (
          <ClipboardCopy value={val}>{val}</ClipboardCopy>
        ),
      };
    }
  }

  const renderRoutes = () =>
    [
      {
        id: DETAILS_TAB_ID,
        element: (
          <DetailsTab
            refetchClusterDetail={refetchClusterDetail}
            clusterDetail={clusterDetail}
            selectedTabIndex={0}
            index={0}
          />
        ),
      },
      {
        id: CLUSTER_DETAIL_URL.URLS,
        element: (
          <UrlsTab
            clusterDetail={clusterDetail}
            selectedTabIndex={1}
            index={1}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.URLS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.STATUS,
        element: (
          <StatusTabNew
            clusterDetail={clusterDetail}
            selectedTabIndex={2}
            index={2}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.STATUS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.NETWORK,
        element: (
          <InfoColumnTab
            selectedTabIndex={3}
            index={3}
            dataPage={formatData(clusterDetail, networkPageProps)}
            nrColumns={3}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.NETWORK}`,
      },
      {
        id: CLUSTER_DETAIL_URL.PERSISTENCE,
        element: (
          <SplitInfoColumnTab
            selectedTabIndex={4}
            index={4}
            dataPage={formatData(clusterDetail, secondPageLeftProps)}
            secondDataPage={formatData(clusterDetail, secondPageRightProps)}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.PERSISTENCE}`,
      },
      {
        id: CLUSTER_DETAIL_URL.PROPERTIES,
        element: (
          <ApplicationPropertiesTab
            selectedTabIndex={5}
            index={5}
            clusterDetail={clusterDetail}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.PROPERTIES}`,
      },
      {
        id: CLUSTER_DETAIL_URL.RESOURCES,
        element: isOrkesUser && (
          <ResourcesTab
            selectedTabIndex={5}
            index={5}
            clusterDetail={clusterDetail}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.RESOURCES}`,
      },
      {
        id: CLUSTER_DETAIL_URL.AUTHENTICATION,
        element: (
          <AuthenticationTab
            selectedTabIndex={5}
            index={5}
            clusterDetail={clusterDetail!}
            showError={showError}
            showSuccess={showSuccess}
            refetchClusterDetail={refetchClusterDetail}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.AUTHENTICATION}`,
      },
      {
        id: CLUSTER_DETAIL_URL.HISTORY,
        element: (
          <ActionsHistoryTab
            entryId={entryId}
            index={6}
            clusterDetail={clusterDetail}
            selectedTabIndex={6}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.HISTORY}/*`,
      },
      {
        id: CLUSTER_DETAIL_URL.PODS,
        element: (
          <PodsTab
            canDeletePods={isOrkesUser}
            index={7}
            clusterDetail={clusterDetail}
            selectedTabIndex={7}
            deletePodAction={(name: string, isLastRunning?: boolean) => {
              setSelectedActionType(CLUSTER_ACTION_TYPE.DELETE_POD);
              setPodName(name);
              setDeletingLastRunningPod(isLastRunning);
            }}
            pullLogsAction={(name: string) => {
              setSelectedActionType(CLUSTER_ACTION_TYPE.PULL_LOGS);
              setPodName(name);
            }}
            downloadAllLogsAction={(name: string) => {
              setSelectedActionType(CLUSTER_ACTION_TYPE.DOWNLOAD_ALL_POD_LOGS);
              setPodName(name);
            }}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.PODS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.RELEASES,
        element: <ReleaseTab selectedTabIndex={8} index={8} />,
        path: `/${CLUSTER_DETAIL_URL.RELEASES}`,
      },
      {
        id: CLUSTER_DETAIL_URL.CERTIFICATE,
        element: (
          <CertificateTab
            selectedTabIndex={9}
            index={9}
            clusterName={clusterDetail?.name}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.CERTIFICATE}`,
      },
      {
        id: CLUSTER_DETAIL_URL.METRICS,
        element: (
          <MetricsTab
            clusterName={clusterDetail?.name}
            organizationId={clusterDetail?.organizationId}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.METRICS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.INFRASTRUCTURE,
        element: (
          <InfrastructureTab
            clusterName={clusterDetail?.name}
            organizationId={clusterDetail?.organizationId}
            orkesAccount={clusterDetail?.orkesAccount}
          />
        ),
        path: `/${CLUSTER_DETAIL_URL.INFRASTRUCTURE}`,
      },
    ]
      .filter(maybeFilterMultiTenantTabs)
      .filter(maybeFilterWorkerHostingTabs)
      .map((route) => (
        <Route
          key={route.id}
          index={!route.path}
          path={route.path}
          element={route.element}
        />
      ));

  const renderTabItems = (cluster: any) =>
    [
      {
        id: DETAILS_TAB_ID,
        label: "Details",
        isHidden: false,
        to: basePath,
      },
      {
        id: CLUSTER_DETAIL_URL.URLS,
        label: "URLs",
        isHidden: false,
        to: `${basePath}/${CLUSTER_DETAIL_URL.URLS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.STATUS,
        label: "Status",
        isHidden: false,
        to: `${basePath}/${CLUSTER_DETAIL_URL.STATUS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.NETWORK,
        label: "Network",
        isHidden: false,
        to: `${basePath}/${CLUSTER_DETAIL_URL.NETWORK}`,
      },
      {
        id: CLUSTER_DETAIL_URL.PERSISTENCE,
        label: "Persistence",
        isHidden: false,
        to: `${basePath}/${CLUSTER_DETAIL_URL.PERSISTENCE}`,
      },
      {
        id: CLUSTER_DETAIL_URL.PROPERTIES,
        label: "Application Properties",
        isHidden: [CLUSTER_STATE.DELETED, CLUSTER_STATE.DELETING].includes(
          cluster?.state as any
        ),
        to: `${basePath}/${CLUSTER_DETAIL_URL.PROPERTIES}`,
      },
      {
        id: CLUSTER_DETAIL_URL.RESOURCES,
        label: "Resources",
        isHidden:
          !isOrkesUser ||
          [CLUSTER_STATE.DELETED, CLUSTER_STATE.DELETING].includes(
            cluster?.state as any
          ),
        to: `${basePath}/${CLUSTER_DETAIL_URL.RESOURCES}`,
      },
      {
        id: CLUSTER_DETAIL_URL.AUTHENTICATION,
        label: "Authentication",
        isHidden: [CLUSTER_STATE.DELETED, CLUSTER_STATE.DELETING].includes(
          cluster?.state as any
        ),
        to: `${basePath}/${CLUSTER_DETAIL_URL.AUTHENTICATION}`,
      },
      {
        id: CLUSTER_DETAIL_URL.HISTORY,
        label: "Actions History",
        isHidden: [CLUSTER_STATE.DELETED, CLUSTER_STATE.DELETING].includes(
          cluster?.state as any
        ),
        to: `${basePath}/${CLUSTER_DETAIL_URL.HISTORY}`,
      },
      {
        id: CLUSTER_DETAIL_URL.PODS,
        label: "Pods",
        // Customer admins use this to confirm a rollout restart finished. Gated to match
        // GET_PODS_DATA, which the tab's Refresh posts. Deleting a pod stays Orkes-only, so that
        // control is not rendered for them rather than shown and refused.
        isHidden:
          !canRunCustomerManageCommand ||
          [CLUSTER_STATE.DELETED, CLUSTER_STATE.DELETING].includes(
            cluster?.state as any
          ),
        to: `${basePath}/${CLUSTER_DETAIL_URL.PODS}`,
      },
      {
        id: CLUSTER_DETAIL_URL.RELEASES,
        label: "Releases",
        isHidden: cluster?.state !== CLUSTER_STATE.PROVISIONED,
        to: `${basePath}/${CLUSTER_DETAIL_URL.RELEASES}`,
      },
      {
        id: CLUSTER_DETAIL_URL.CERTIFICATE,
        label: "Certificates",
        // customCertificates/getAll accepts environmentManagement, globalBypass or
        // supportOperationalAccess.
        isHidden:
          !canManageCertificates ||
          cluster?.state !== CLUSTER_STATE.PROVISIONED,
        to: `${basePath}/${CLUSTER_DETAIL_URL.CERTIFICATE}`,
      },
      {
        id: CLUSTER_DETAIL_URL.INFRASTRUCTURE,
        label: "Infrastructure",
        // Guarded more narrowly than the rest of Mission Control: /infrastructure requires
        // globalBypass or support operational access, so even environmentView customers would
        // only see the load error. Hidden for anyone but Orkes staff, same as Metrics.
        isHidden: !isOrkesUser,
        to: `${basePath}/${CLUSTER_DETAIL_URL.INFRASTRUCTURE}`,
      },
      {
        id: CLUSTER_DETAIL_URL.METRICS,
        label: "Metrics",
        // Orkes-only, like Resources: the endpoint behind this tab is guarded by globalBypass or
        // support access, so a customer opening it would only ever see the load error.
        // Also needs a running cluster for there to be data, same as Certificates.
        isHidden: !isOrkesUser || cluster?.state !== CLUSTER_STATE.PROVISIONED,
        to: `${basePath}/${CLUSTER_DETAIL_URL.METRICS}`,
      },
    ]
      .filter(maybeFilterMultiTenantTabs)
      .filter(maybeFilterWorkerHostingTabs)
      .map((tab, index) =>
        tab.isHidden ? null : (
          <StyledTab
            key={tab.id}
            value={tab.label}
            component={Link}
            to={tab.to}
            label={tab.label}
            {...a11yProps(index)}
          />
        )
      );

  if (!isOrkesUser && clusterDetail?.environmentType === "TRIAL") {
    return <Navigate to={UNAUTHORIZED_ROUTE_URL} replace />;
  }

  const handleSelectedActionType = (value: string) => {
    setSelectedActionType(value);
  };
  const handleChangePodName = (value: string) => {
    setPodName(value);
    // Chosen from the actions menu, so the tab's answer no longer applies to it.
    setDeletingLastRunningPod(undefined);
  };

  return (
    <ChangeProtectionGuardProvider
      enabled={Boolean(clusterDetail?.changeProtection)}
    >
      <Helmet>
        <title>
          {isWorkerHosting ? "Worker Hosting" : "Cluster Details"}:{" "}
          {clusterName}
        </title>
      </Helmet>
      {clusterDetailFetching && <LinearProgress />}

      {!clusterDetailFetching && clusterDetail ? (
        <Box style={{ padding: 5, paddingLeft: 25, paddingTop: 25 }}>
          {/* A column, not a row: the name and its chips need the full width. Sharing one
              row with CONNECT and the action selector left the heading 732px of 1170, and
              the state chip wrapped once a fourth attribute chip appeared. Stacking gives
              the chips the whole row and costs only the controls' vertical space. */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Heading level={2} id="cluster-name-section">
              {isWorkerHosting ? "Worker Hosting" : "Cluster"}:{" "}
              {clusterDetail.name}{" "}
              {/* Leads the chip row here too, for the same reason as the listing card:
                  it says what the entity IS, so it precedes the attribute chips */}
              {isWorkerHosting && (
                <HostedWorkersComputeChip sx={{ marginRight: "16px" }} />
              )}
              <Chip
                label={`${
                  clusterDetail.environmentType
                    ? clusterDetail.environmentType
                    : "PRODUCTION"
                }`}
                color={"info"}
                variant={"outlined"}
                sx={{
                  ...sxChip,
                  display: "inline-flex",
                  width: "fit-content",
                  alignItems: "center",
                }}
                avatar={
                  <Avatar
                    sx={{
                      margin: 0,
                      padding: 0,
                      marginTop: "-3px",
                      "& img": {
                        width: 18,
                        height: 18,
                      },
                    }}
                    alt="env"
                    src={getEnvironmentIcon(clusterDetail.environmentType)}
                  />
                }
              />
              <HostingChip
                cluster={clusterDetail}
                sx={{ marginLeft: "16px" }}
              />
              {/* An attribute of the cluster, so it sits with the other attribute chips and
                  ahead of the state chip, which stays last. Renders nothing unless the cluster
                  is actually peered. */}
              <MultiRegionChip
                cluster={clusterDetail}
                sx={{ marginLeft: "16px" }}
              />
              <Tooltip title={"Click to monitor progress of the provisioning"}>
                <Link
                  to={`/home/clusterprogess?clusterName=${clusterDetail.name}`}
                >
                  <Chip
                    sx={{
                      ...clickableChipSx,
                      marginLeft: 4,
                      cursor: "pointer",
                    }}
                    label={
                      clusterDetail.state === "SCHEDULE_DELETION"
                        ? "DELETION SCHEDULED"
                        : clusterDetail.state ?? "NA"
                    }
                    color={getColorFromState(clusterDetail.state)}
                    icon={<IconFromState state={clusterDetail.state} />}
                  />
                </Link>
              </Tooltip>
            </Heading>
            {!clusterWasRemoved && !isMultiTenantAndNotOrkesUser ? (
              // Grouped so Connect sits immediately left of Actions. alignSelf rather than
              // marginLeft:auto now that the parent is a column -- auto margins would size
              // this against the cross axis and a stretched item cannot be pushed right.
              // ActionTypeSelector keeps its own margin, inert here because the wrapper is
              // sized to its content.
              <Box
                sx={{
                  alignSelf: "flex-end",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                {isOrkesUser && (
                  // Styled as one of the heading chips rather than a button, so it reads as part
                  // of the same row as the environment and state chips beside it.
                  <Chip
                    variant="outlined"
                    clickable
                    onClick={() => setConnectOpen(true)}
                    label="CONNECT"
                    color="primary"
                    icon={<PowerSettingsNewIcon />}
                    sx={{
                      ...clickableChipSx,
                      height: 36,
                      cursor: "pointer",
                    }}
                  />
                )}
                <ActionTypeSelector
                  isOrkesUser={isOrkesUser}
                  hasAdminOrRootRole={hasAdminOrRootRole}
                  hasEngineeringProfile={hasEngineeringProfile}
                  hasGlobalBypass={hasGlobalBypass}
                  canManageEnvironment={canManageEnvironment}
                  canUseSupportOperations={canUseSupportOperations}
                  clusterDetail={clusterDetail}
                  refetchClusterDetail={refetchClusterDetail}
                  selectedActionType={selectedActionType}
                  podName={podName}
                  deletingLastRunningPod={deletingLastRunningPod}
                  handleSelectedActionType={handleSelectedActionType}
                  handleChangePodName={handleChangePodName}
                />
              </Box>
            ) : null}
            <ConnectDialog
              open={connectOpen}
              onClose={() => setConnectOpen(false)}
              clusterName={clusterDetail?.name}
              organizationId={clusterDetail?.organizationId}
            />
          </div>
          <Paper
            elevation={1}
            id="cluster-details-container"
            style={{
              marginRight: 10,
              marginTop: 16,
            }}
          >
            <StyledTabs
              sx={{
                width: { sm: "700px", md: "900px", lg: "auto", xl: "auto" },
              }}
              scrollButtons={true}
              variant={"scrollable"}
              value={selectedTabIndex(relativePath)}
              aria-label="basic tabs example"
            >
              {renderTabItems(clusterDetail)}
            </StyledTabs>
            <Routes>{renderRoutes()}</Routes>
            <Box
              sx={{
                display: "flex",
                borderBottom: "1px solid lightgrey",
                padding: "16px",
              }}
            >
              <Link
                style={{
                  marginLeft: "auto",
                }}
                to="/home"
              >
                <OutlinedButton id="return-home-btn">
                  Return Home
                </OutlinedButton>
              </Link>
            </Box>
          </Paper>
        </Box>
      ) : null}
    </ChangeProtectionGuardProvider>
  );
}
